import { useLazyQuery, useMutation, useQuery } from "@apollo/client";
import { useAlerts } from "components/Alerts/AlertProvider";
import { useUpdateActivityAssignees } from "hooks/activities";
import GET_DOCUMENT_DOWNLOAD_LINK_QUERY from "queries/getDocumentDownloadLink";
import { useCallback } from "react";
import { downloadFromUrl } from "util/download";

import {
  ADD_CUSTOM_WORK_STEP,
  ATTACH_DOCUMENT_TO_CUSTOM_WORK,
  COMPLETE_CUSTOM_WORK_STEP,
  CUSTOM_WORK_ITEM_QUERY,
  REMOVE_DOCUMENT_FROM_CUSTOM_WORK,
  REOPEN_CUSTOM_WORK_STEP,
  SKIP_CUSTOM_WORK_STEP,
  START_CUSTOM_WORK_STEP,
} from "./api";

/**
 * Every server interaction the custom deliverable screen needs: the detail
 * query, the step mutations, document attach/detach, assignee updates, and
 * the download-link fetch. Split out so the screen component is layout rather
 * than wiring — the hook owns the Apollo surface and the shared error alert.
 */
export function useCustomWorkDetail(workflowId: string | undefined) {
  const alerts = useAlerts();

  const { data, loading, error, refetch } = useQuery(CUSTOM_WORK_ITEM_QUERY, {
    variables: { workflowId: workflowId ?? "" },
    skip: !workflowId,
  });

  const mutationOptions = {
    onError: (mutationError: Error) => alerts.error("That didn't stick", mutationError),
  };
  const [addStepMutation, { loading: adding }] = useMutation(ADD_CUSTOM_WORK_STEP, mutationOptions);
  const [startStep, { loading: starting }] = useMutation(START_CUSTOM_WORK_STEP, mutationOptions);
  const [completeStep, { loading: completing }] = useMutation(
    COMPLETE_CUSTOM_WORK_STEP,
    mutationOptions
  );
  const [skipStep, { loading: skipping }] = useMutation(SKIP_CUSTOM_WORK_STEP, mutationOptions);
  const [reopenStep, { loading: reopening }] = useMutation(
    REOPEN_CUSTOM_WORK_STEP,
    mutationOptions
  );
  const [attachDocument] = useMutation(ATTACH_DOCUMENT_TO_CUSTOM_WORK, mutationOptions);
  const [removeDocument] = useMutation(REMOVE_DOCUMENT_FROM_CUSTOM_WORK, mutationOptions);
  const [updateAssignees] = useUpdateActivityAssignees(() => {
    void refetch();
  });
  const [getDocumentDownloadLink] = useLazyQuery(GET_DOCUMENT_DOWNLOAD_LINK_QUERY, {
    onCompleted: (linkData) => {
      if (!linkData?.getDocumentDownloadLink) {
        alerts.error("Error fetching download link");
        return;
      }
      // The signed URL downloads (attachment disposition); an anchor click
      // needs no popup allowance, unlike window.open outside the gesture.
      downloadFromUrl(linkData.getDocumentDownloadLink);
    },
    onError: (linkError) => alerts.error("Error fetching download link", linkError),
  });

  /**
   * The one write whose caller needs the outcome: the add-a-step field clears
   * only once the step is really on the deliverable, so a refusal (closed
   * deliverable, blank description) leaves the text where staff can fix it while
   * the shared alert says why.
   */
  const addStep = useCallback(
    async (description: string): Promise<boolean> => {
      if (!workflowId) {
        return false;
      }
      const result = await addStepMutation({ variables: { workflowId, description } });
      return result.data?.addCustomWorkStep != null;
    },
    [addStepMutation, workflowId]
  );

  return {
    detail: data?.customWorkItem,
    loading,
    error,
    refetch,
    busy: adding || starting || completing || skipping || reopening,
    addStep,
    startStep,
    completeStep,
    skipStep,
    reopenStep,
    attachDocument,
    removeDocument,
    updateAssignees,
    getDocumentDownloadLink,
  };
}
